import {
  createContext,
  Dispatch,
  SetStateAction,
  useEffect,
  useMemo,
  useState,
} from "react";

import {
  addHours,
  getMinutes,
  getSeconds,
  setMinutes,
  startOfMinute,
} from "date-fns";
import type { InfraEventPassengerCount } from "features/CenterContent/RoleContent/AlternativeTransport/DecisionSupport/components/AreaChart/types";
import { Nullish } from "shared/utils/objectUtils";

export type DecisionSupportContextValues = {
  timeHorizonHours: 2 | 4 | 6 | 8;
  horizonFromTime: Date;
  horizonToTime: Date;
  transportNeedData: InfraEventPassengerCount[];
  transportNeedMaxYValue: number;
  totalAffected: Nullish<number>;
  affectedWithWheelchair: Nullish<number>;
  isTransportNeedPending: boolean;
  isTransportNeedError: boolean;
  refetchTransportNeed: () => void;
  isAffectedPending: boolean;
  isAffectedError: boolean;
};

type DecisionSupportContextSetters = {
  setTimeHorizonHours: Dispatch<
    SetStateAction<DecisionSupportContextValues["timeHorizonHours"]>
  >;
  setTotalAffected: Dispatch<
    SetStateAction<DecisionSupportContextValues["totalAffected"]>
  >;
  setAffectedWithWheelchair: Dispatch<
    SetStateAction<DecisionSupportContextValues["affectedWithWheelchair"]>
  >;
  setIsAffectedPending: Dispatch<SetStateAction<boolean>>;
  setIsAffectedError: Dispatch<SetStateAction<boolean>>;
};

export type DecisionSupportContextType = DecisionSupportContextValues &
  DecisionSupportContextSetters;

const defaultValues: DecisionSupportContextType = {
  timeHorizonHours: 2,
  horizonFromTime: new Date(),
  horizonToTime: new Date(),
  transportNeedData: [],
  transportNeedMaxYValue: 0,
  totalAffected: null,
  affectedWithWheelchair: null,
  isTransportNeedPending: false,
  isTransportNeedError: false,
  refetchTransportNeed: () => {},
  isAffectedPending: true,
  isAffectedError: false,
  setTimeHorizonHours: () => {},
  setTotalAffected: () => {},
  setAffectedWithWheelchair: () => {},
  setIsAffectedPending: () => {},
  setIsAffectedError: () => {},
};

function previousWholeQuarterHour(time: Date) {
  const previousWholeMinute = startOfMinute(time);
  const minutes = getMinutes(previousWholeMinute);

  if (minutes < 15) return setMinutes(previousWholeMinute, 0);
  if (minutes < 30) return setMinutes(previousWholeMinute, 15);
  if (minutes < 45) return setMinutes(previousWholeMinute, 30);
  return setMinutes(previousWholeMinute, 45);
}

const lastWholeQuarterHour = () => previousWholeQuarterHour(new Date());

function secondsToNextWholeQuarterHour() {
  const currentTime = new Date();
  const seconds = getMinutes(currentTime) * 60 + getSeconds(currentTime);

  if (seconds <= 15 * 60) return 15 * 60 - seconds;
  if (seconds <= 30 * 60) return 30 * 60 - seconds;
  if (seconds <= 45 * 60) return 45 * 60 - seconds;
  if (seconds <= 60 * 60) return 60 * 60 - seconds;
  return 1; // In case of leap seconds!
}

export const useDecisionSupportContextValues = () => {
  const [showAllStretches, setShowAllStretches] = useState(false);
  const [timeHorizonHours, setTimeHorizonHours] = useState<2 | 4 | 6 | 8>(2);

  // The timestamp from which we compute horizon times
  const [baseTime, setBaseTime] = useState(lastWholeQuarterHour);

  const horizonFromTime = useMemo(() => baseTime, [baseTime]);
  const horizonToTime = useMemo(
    () => addHours(horizonFromTime, timeHorizonHours),
    [horizonFromTime, timeHorizonHours],
  );

  // Affected passengers
  const [totalAffected, setTotalAffected] = useState<Nullish<number>>(null);
  const [affectedWithWheelchair, setAffectedWithWheelchair] =
    useState<Nullish<number>>(null);
  const [isAffectedPending, setIsAffectedPending] = useState(true);
  const [isAffectedError, setIsAffectedError] = useState(false);

  useEffect(() => {
    // Automatically update the time horizon.
    // * First, set a timeout for the next whole quarter hour
    // * Then, once the timeout hits, set an interval for every 15 minutes after that
    const secondsToNextQuarterHour = secondsToNextWholeQuarterHour();

    let intervalId: Nullish<ReturnType<typeof setInterval>> = null;

    const timeoutId = setTimeout(
      () => {
        setBaseTime(lastWholeQuarterHour);

        intervalId = setInterval(
          () => setBaseTime(lastWholeQuarterHour),
          15 * 60 * 1000,
        ); // Editor wants to use the object type from node, but this is actually a number in browser
      },
      secondsToNextQuarterHour * 1000, // Set a timeout on the next full quarter hour
    );

    // Add events to update the filtering logic whenever the tab is focused, in case it's been hibernating
    const controller = new AbortController();

    window.addEventListener("focus", () => setBaseTime(lastWholeQuarterHour), {
      signal: controller.signal,
    });

    document.addEventListener(
      "visibilitychange",
      () => {
        if (!document.hidden) {
          setBaseTime(lastWholeQuarterHour);
        }
      },
      { signal: controller.signal },
    );

    // Cleanup on unmount
    return () => {
      // Stop timers
      if (intervalId) {
        clearInterval(intervalId);
      } else {
        clearTimeout(timeoutId);
      }
      // Remove event listeners
      controller.abort();
    };
  }, []);

  return {
    showAllStretches,
    setShowAllStretches,
    timeHorizonHours,
    setTimeHorizonHours,
    horizonFromTime,
    horizonToTime,
    totalAffected,
    setTotalAffected,
    affectedWithWheelchair,
    setAffectedWithWheelchair,
    isAffectedPending,
    setIsAffectedPending,
    isAffectedError,
    setIsAffectedError,
  };
};

const DecisionSupportContext = createContext(defaultValues);

export default DecisionSupportContext;
